import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './icons/Icon';
import './Services.css';
import { getPageContent } from '../services/pageContentService';
import { usePageContent } from '../hooks/usePageContent';
import { resolveIcon } from './icons/iconRegistry';

const HEADER_DEFAULTS = {
  title: 'Comprehensive Dental Care',
  description: 'We offer a full range of dental services to meet all your oral health needs, from routine care to advanced treatments.',
};

const SERVICE_IMAGES = {
  'general-checkup': 'https://images.unsplash.com/photo-1606811971618-4486d14f4bad?w=600&q=80',
  'cosmetic-dentistry': 'https://images.unsplash.com/photo-1598256989800-fe5f95da3ff0?w=600&q=80',
  'orthodontics': 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=600&q=80',
  'dental-implants': 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&q=80',
  'teeth-whitening': 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&q=80',
  'emergency-care': 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600&q=80',
};

const Services = ({ hideHeader = false, variant = 'default' }) => {
  const [remoteCards, setRemoteCards] = useState(null);
  const { content: header } = usePageContent('services', 'header', HEADER_DEFAULTS);

  const defaultServicesData = useMemo(() => ([
    {
      id: 'general-checkup',
      icon: 'stethoscope',
      title: 'General Dentistry',
      description: 'Comprehensive dental care including routine checkups, cleanings, fillings, and preventive care to keep your smile healthy.',
      link: '/services/general-checkup',
    },
    {
      id: 'cosmetic-dentistry',
      icon: 'sparkles',
      title: 'Cosmetic Dentistry',
      description: 'Transform your smile with professional whitening, veneers, bonding, and advanced cosmetic solutions tailored to you.',
      link: '/services/cosmetic-dentistry',
    },
    {
      id: 'orthodontics',
      icon: 'wrench',
      title: 'Orthodontics',
      description: 'Modern braces and clear aligners to help you achieve a perfectly aligned smile with comfort and precision.',
      link: '/services/orthodontics',
    },
    {
      id: 'dental-implants',
      icon: 'bone',
      title: 'Dental Implants',
      description: 'Durable and natural-looking implants that restore function and aesthetics, giving you back your confident smile.',
      link: '/services/dental-implants',
    },
    {
      id: 'teeth-whitening',
      icon: 'smile',
      title: 'Teeth Whitening',
      description: 'Professional-grade whitening treatments to brighten your smile safely and effectively in our office.',
      link: '/services/teeth-whitening',
    },
    {
      id: 'emergency-care',
      icon: 'siren',
      title: 'Emergency Care',
      description: 'Immediate dental care when you need it most. We\'re here to help with urgent dental issues and pain relief.',
      link: '/services/emergency-care',
    },
  ]), []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await getPageContent('services', 'cards');
        const items = data?.items;
        if (!isMounted) return;
        if (Array.isArray(items)) {
          const enabled = items
            .filter((x) => x && x.enabled !== false)
            .map((x) => ({
              id: x.id || x.title || x.link,
              title: x.title || '',
              description: x.description || '',
              icon: x.icon || 'stethoscope',
              imageUrl: x.imageUrl || '',
              link: x.link || '',
            }))
            .filter((x) => x.title.trim() && x.description.trim() && x.link.trim());
          setRemoteCards(enabled.length ? enabled : []);
        } else {
          setRemoteCards([]);
        }
      } catch (e) {
        if (isMounted) setRemoteCards([]);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const servicesData = (remoteCards && remoteCards.length > 0) ? remoteCards : defaultServicesData;
  const sectionClass = `services-section ${variant === 'page' ? 'services-section--page' : ''}`;

  return (
    <section className={sectionClass} id="services">
      {!hideHeader && (
        <div className="services-header">
          <span className="section-eyebrow">Our Services</span>
          <h2 className="services-title">{header.title}</h2>
          <p className="services-description">{header.description}</p>
        </div>
      )}

      <div className="services-grid">
        {servicesData.map((service, index) => {
          const imageUrl = service.imageUrl || SERVICE_IMAGES[service.id];
          return (
            <article key={service.id || index} className="service-card">
              {imageUrl && (
                <div className="service-card-image">
                  <img src={imageUrl} alt="" loading="lazy" />
                  <div className="service-card-image-overlay" />
                </div>
              )}
              <div className="service-card-body">
                <div className="service-icon">
                  <Icon name={resolveIcon(service.icon, service.id)} size={28} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link
                  to={service.link || `/services/${service.id}`}
                  className="service-link"
                >
                  Learn More
                  <Icon name="chevronRight" size={16} />
                </Link>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default Services;
